import { useRef, useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { rememberEngineerSchools, useAuthStore } from '@/store'
import { ApiErrorResponse } from '@/services/api/client'
import { resolvePostLoginPath } from '@/utils/authRedirect'
import { isPhoneDevice } from '@/utils/talkWelt'

const errorMessages: Record<string, string> = {
  'bereits verwendet': 'Dieser Login-Link wurde bereits verwendet. Bitte fordern Sie einen neuen Login-Link an.',
  'neuerer Login-Link': 'Ein neuerer Login-Link wurde gesendet. Bitte verwenden Sie die neueste E-Mail.',
  'abgelaufen': 'Dieser Login-Link ist abgelaufen. Bitte fordern Sie einen neuen Login-Link an.',
  'nicht mehr gültig': 'Dieser Login-Link ist nicht mehr gültig. Bitte fordern Sie einen neuen Login-Link an.',
  'Ungültiger Token': 'Login-Link nicht gefunden. Bitte fordern Sie einen neuen Login-Link an.',
  'deaktiviert': 'Dieses Konto ist deaktiviert. Bitte kontaktieren Sie die Administration.',
}

const verifyingTokens = new Set<string>()

export function VerifyPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { verify, isLoading, clearError } = useAuthStore()
  const [verifying, setVerifying] = useState(false)
  const [displayError, setDisplayError] = useState<string | null>(null)
  const attemptedRef = useRef(false)
  const token = searchParams.get('token')

  async function handleVerify() {
    if (!token) {
      setDisplayError('Kein Token gefunden. Bitte fordern Sie einen neuen Login-Link an.')
      return
    }
    if (attemptedRef.current || verifyingTokens.has(token)) return
    attemptedRef.current = true
    verifyingTokens.add(token)
    setVerifying(true)
    setDisplayError(null)
    clearError()

    try {
      const response = await verify(token)
      if (rememberEngineerSchools(response)) {
        navigate('/engineer/schools', { replace: true })
        return
      }
      navigate(resolvePostLoginPath(useAuthStore.getState().user?.role), { replace: true })
    } catch (e: unknown) {
      setVerifying(false)
      verifyingTokens.delete(token)
      attemptedRef.current = false
      let errorDetail = ''
      if (e instanceof ApiErrorResponse) {
        errorDetail = e.detail
      } else if (e instanceof Error) {
        errorDetail = e.message
      }
      let matchedMessage = 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.'
      for (const [key, message] of Object.entries(errorMessages)) {
        if (errorDetail.includes(key)) {
          matchedMessage = message
          break
        }
      }
      setDisplayError(matchedMessage)
    }
  }

  const dark = isPhoneDevice()
  const shell = dark ? 'min-h-screen flex items-center justify-center bg-[#081018] text-slate-100' : 'min-h-screen flex items-center justify-center bg-gray-50'
  const titleCls = dark ? 'text-2xl font-bold text-slate-100 mb-2' : 'text-2xl font-bold text-gray-900 mb-2'
  const bodyCls = dark ? 'text-slate-400 mb-6' : 'text-gray-600 mb-6'

  if (!token && !displayError) {
    return (
      <div className={shell}>
        <div className="max-w-md w-full text-center py-8">
          <div className="text-red-500 text-6xl mb-4">✗</div>
          <h1 className={titleCls}>Anmeldung fehlgeschlagen</h1>
          <p className={bodyCls}>Kein Token gefunden. Bitte fordern Sie einen neuen Login-Link an.</p>
          <Link to="/login" className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium">
            Neuer Login-Link
          </Link>
        </div>
      </div>
    )
  }

  if (verifying || isLoading) {
    return (
      <div className={shell}>
        <div className="max-w-md w-full text-center py-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className={`${dark ? 'text-slate-300' : 'text-gray-600'} text-lg`}>Anmeldung wird verarbeitet...</p>
        </div>
      </div>
    )
  }

  if (displayError) {
    return (
      <div className={shell}>
        <div className="max-w-md w-full text-center py-8">
          <div className="text-red-500 text-6xl mb-4">✗</div>
          <h1 className={titleCls}>Anmeldung fehlgeschlagen</h1>
          <p className={bodyCls}>{displayError}</p>
          <div className="space-x-4">
            <Link
              to="/login"
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Neuer Login-Link
            </Link>
            <Link
              to="/"
              className={dark
                ? 'inline-block bg-slate-800 text-slate-100 px-6 py-3 rounded-lg hover:bg-slate-700 transition-colors font-medium'
                : 'inline-block bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium'}
            >
              Zur Startseite
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={shell}>
      <div className="max-w-md w-full text-center py-8 px-4">
        <h1 className={titleCls}>Anmeldung bestätigen</h1>
        <p className={bodyCls}>
          Klicken Sie auf den Button, um sich anzumelden. Der Link allein reicht nicht — so bleibt der Login-Link gültig, auch wenn der E-Mail-Scanner ihn vorher öffnet.
        </p>
        <button
          type="button"
          onClick={handleVerify}
          className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Jetzt anmelden
        </button>
      </div>
    </div>
  )
}
